import { useEffect, useRef, useState } from "react";

/**
 * Polls `ref.current` via `select` until it returns non-null, then holds
 * that value as reactive state (re-polls whenever `ref`/`select` identity
 * changes). Generalizes the same pattern `use-map-ready.ts` needs for the
 * maplibre instance itself — anything read off an imperative handle whose
 * readiness has no callback (`LogisticsMapHandle.map`, `.routes`, `.orders`
 * all work this way) needs this to become a real effect dependency rather
 * than a stale read at render time.
 */
export function usePolledRef<H, T>(ref: React.RefObject<H | null>, select: (handle: H) => T | null): T | null {
  const [value, setValue] = useState<T | null>(null);
  const rafRef = useRef(0);
  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    const current = ref.current ? selectRef.current(ref.current) : null;
    if (current) {
      setValue(current);
      return;
    }
    const tick = (): void => {
      const v = ref.current ? selectRef.current(ref.current) : null;
      if (v) setValue(v);
      else rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [ref]);

  return value;
}
